import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const BOT_UA_RE = /bot|crawler|spider|headless|preview/i;
function isBotUserAgent(): boolean {
  const ua = getRequestHeader("user-agent") ?? "";
  return BOT_UA_RE.test(ua);
}


export type VisitRange = "7d" | "1m" | "3m" | "1y" | "5y" | "all";
export type VisitKind = "shop" | "product" | "site" | "article" | "email" | "phone";


export const trackVendorVisit = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        vendorId: z.string().uuid(),
        kind: z.enum(["shop", "product", "email", "phone"]),
        productId: z.string().uuid().nullable().optional(),
        path: z.string().max(500).nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    if (isBotUserAgent()) return { ok: true as const, skipped: true as const };
    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { error } = await supabase.from("vendor_visits").insert({
      vendor_id: data.vendorId,
      kind: data.kind,
      product_id: data.productId ?? null,
      path: data.path ?? null,
    });
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });

export const trackPageVisit = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        kind: z.enum(["site", "article"]),
        path: z.string().max(500).nullable().optional(),
        articleSlug: z.string().max(200).nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    if (isBotUserAgent()) return { ok: true as const, skipped: true as const };
    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { error } = await supabase.from("vendor_visits").insert({
      vendor_id: null,
      kind: data.kind,
      path: data.path ?? null,
      article_slug: data.articleSlug ?? null,
    });
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });

function rangeStart(range: VisitRange): Date | null {
  const now = new Date();
  if (range === "all") return null;
  const d = new Date(now);
  if (range === "7d") d.setDate(d.getDate() - 7);
  else if (range === "1m") d.setMonth(d.getMonth() - 1);
  else if (range === "3m") d.setMonth(d.getMonth() - 3);
  else if (range === "1y") d.setFullYear(d.getFullYear() - 1);
  else if (range === "5y") d.setFullYear(d.getFullYear() - 5);
  return d;
}

export type VisitStats = {
  total: number;
  shop: number;
  product: number;
  series: { date: string; count: number }[];
  granularity: "day" | "month";
};

export const getMyVendorVisitStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ range: z.enum(["7d","1m","3m","1y","5y","all"]) }).parse(input),
  )
  .handler(async ({ data, context }): Promise<VisitStats> => {
    const { supabase, userId } = context;

    const { data: vendor, error: vErr } = await supabase
      .from("vendors")
      .select("id, profile_id, profiles:profile_id!inner(auth_user_id)")
      .eq("profiles.auth_user_id", userId)
      .maybeSingle();
    if (vErr) throw vErr;
    if (!vendor) {
      return { total: 0, shop: 0, product: 0, series: [], granularity: "day" };
    }

    const since = rangeStart(data.range);
    let q = supabase
      .from("vendor_visits")
      .select("kind, visited_at")
      .eq("vendor_id", (vendor as { id: string }).id)
      .in("kind", ["shop", "product"])
      .order("visited_at", { ascending: true })
      .limit(50000);
    if (since) q = q.gte("visited_at", since.toISOString());
    const { data: rows, error } = await q;
    if (error) throw error;

    const visits = (rows ?? []) as { kind: string; visited_at: string }[];
    const total = visits.length;
    const shop = visits.filter((v) => v.kind === "shop").length;
    const product = visits.filter((v) => v.kind === "product").length;

    const granularity: "day" | "month" = data.range === "1m" ? "day" : "month";
    const buckets = new Map<string, number>();
    for (const v of visits) {
      const d = new Date(v.visited_at);
      const key =
        granularity === "day"
          ? d.toISOString().slice(0, 10)
          : `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      buckets.set(key, (buckets.get(key) ?? 0) + 1);
    }
    const series = [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({ date, count }));

    return { total, shop, product, series, granularity };
  });

export type AdminVisitStats = {
  totals: { site: number; shop: number; product: number; article: number };
  series: { date: string; site: number; shop: number; product: number; article: number }[];
  granularity: "day" | "month";
};

export const getAdminVisitStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ range: z.enum(["7d","1m","3m","1y","5y","all"]) }).parse(input),
  )
  .handler(async ({ data, context }): Promise<AdminVisitStats> => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const since = rangeStart(data.range);
    const granularity: "day" | "month" =
      data.range === "7d" || data.range === "1m" || data.range === "3m" ? "day" : "month";

    const { data: rows, error } = await supabaseAdmin.rpc("admin_visit_series", {
      _since: (since ? since.toISOString() : null) as unknown as string,
      _granularity: granularity,
    });
    if (error) throw error;

    const totals = { site: 0, shop: 0, product: 0, article: 0 };
    const buckets = new Map<string, { site: number; shop: number; product: number; article: number }>();
    for (const r of (rows ?? []) as { bucket: string; kind: string; cnt: number }[]) {
      const n = Number(r.cnt) || 0;
      if (r.kind in totals) totals[r.kind as keyof typeof totals] += n;
      const b = buckets.get(r.bucket) ?? { site: 0, shop: 0, product: 0, article: 0 };
      if (r.kind in b) b[r.kind as keyof typeof b] += n;
      buckets.set(r.bucket, b);
    }
    const series = [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, v]) => ({ date, ...v }));

    return { totals, series, granularity };
  });

export type VisitBreakdownItem = {
  key: string;
  label: string;
  sublabel?: string | null;
  href?: string | null;
  count: number;
};

export const getAdminVisitBreakdown = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        kind: z.enum(["site", "shop", "product", "article"]),
        range: z.enum(["7d","1m","3m","1y","5y","all"]),
        limit: z.number().int().min(1).max(200).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<VisitBreakdownItem[]> => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const since = rangeStart(data.range);
    const limit = data.limit ?? 50;

    const { data: aggRows, error: aggErr } = await supabaseAdmin.rpc("admin_visit_top", {
      _kind: data.kind,
      _since: (since ? since.toISOString() : null) as unknown as string,
      _limit: limit,
    });
    if (aggErr) throw aggErr;
    const agg = ((aggRows ?? []) as { key: string; cnt: number }[])
      .map((r) => ({ key: r.key, count: Number(r.cnt) || 0 }));

    if (agg.length === 0) return [];

    if (data.kind === "site") {
      return agg.map((r) => ({ key: r.key, label: r.key || "/", href: r.key || "/", count: r.count }));
    }

    if (data.kind === "article") {
      return agg.map((r) => ({
        key: r.key,
        label: r.key,
        href: r.key.startsWith("/") ? r.key : `/clanek/${r.key}`,
        count: r.count,
      }));
    }

    if (data.kind === "shop") {
      const ids = agg.map((r) => r.key);
      const { data: vendors } = await supabaseAdmin
        .from("vendors")
        .select("id, store_name, slug, city")
        .in("id", ids);
      const vmap = new Map((vendors ?? []).map((v) => [v.id as string, v]));
      return agg.map((r) => {
        const v = vmap.get(r.key);
        return {
          key: r.key,
          label: (v?.store_name as string) ?? "(izbrisana trgovina)",
          sublabel: (v?.city as string) ?? null,
          href: v?.slug ? `/prodajalci/${v.slug}` : null,
          count: r.count,
        };
      });
    }

    // product
    const ids = agg.map((r) => r.key);
    const { data: products } = await supabaseAdmin
      .from("products")
      .select("id, title, slug, vendors:vendor_id(store_name)")
      .in("id", ids);
    const pmap = new Map((products ?? []).map((p) => [p.id as string, p]));
    return agg.map((r) => {
      const p = pmap.get(r.key) as { title?: string; slug?: string; vendors?: { store_name?: string } | null } | undefined;
      return {
        key: r.key,
        label: p?.title ?? "(izbrisan izdelek)",
        sublabel: p?.vendors?.store_name ?? null,
        href: p?.slug ? `/izdelki/${p.slug}` : null,
        count: r.count,
      };
    });
  });

export type ContactStats = { emailClicks: number; phoneClicks: number; inquiries: number };

export const getAdminContactStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ range: z.enum(["7d","1m","3m","1y","5y","all"]) }).parse(input),
  )
  .handler(async ({ data, context }): Promise<ContactStats> => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const since = rangeStart(data.range);
    const sinceIso = since ? since.toISOString() : null;

    const emailQ = supabaseAdmin.from("vendor_visits").select("id", { count: "exact", head: true }).eq("kind", "email");
    const phoneQ = supabaseAdmin.from("vendor_visits").select("id", { count: "exact", head: true }).eq("kind", "phone");
    const inqQ = supabaseAdmin.from("product_inquiries").select("id", { count: "exact", head: true });

    const [e, p, i] = await Promise.all([
      sinceIso ? emailQ.gte("visited_at", sinceIso) : emailQ,
      sinceIso ? phoneQ.gte("visited_at", sinceIso) : phoneQ,
      sinceIso ? inqQ.gte("created_at", sinceIso) : inqQ,
    ]);

    return {
      emailClicks: e.count ?? 0,
      phoneClicks: p.count ?? 0,
      inquiries: i.count ?? 0,
    };
  });


